// "/pure": the default entry injects js.stripe.com/v3 the moment this module
// is imported, and this one is reachable from Layout (ReturnBar ->
// UpgradeModal -> CreditCheckoutModal), so every visitor on every page paid
// ~270KB of Stripe.js and ~70-80ms of main thread (Lighthouse, mobile) before
// anyone opened a checkout. The pure entry loads it on the first loadStripe().
import { loadStripe } from "@stripe/stripe-js/pure";
import StripePaymentElement from "../StripePaymentElement";
import { Elements } from "@stripe/react-stripe-js";
import { Dialog, DialogContent } from "@jammable/ui-components/Dialog";
import { Lock, X } from "lucide-react";
import { LOCALE_META, useLocale } from "@jammable/ui-core/src/i18n";

export const CreditCheckout = (props) => {
  const {
    newLayout,
    clientSecret,
    symbol,
    currency,
    subType,
    price,
    intentType,
    dark = false,
    submitLabel,
    formClassName,
    submitWrapClassName,
    footer,
    onReady,
    express = false,
  } = props;

  const stripePromise = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
  );
  // Stripe picks the browser's language by default ("auto"). Someone who
  // chose Spanish or Portuguese on the site gets the form in it too.
  const { locale } = useLocale();

  // Design language F inside Stripe's iframe: Manrope, ink text on a
  // lavender field, purple focus ring, rounded fields, no shadows.
  const stripeoptions = {
    clientSecret: clientSecret,
    loader: "always",
    ...(LOCALE_META[locale].stripe
      ? { locale: LOCALE_META[locale].stripe }
      : {}),
    fonts: [
      {
        cssSrc:
          "https://fonts.googleapis.com/css2?family=Manrope:wght@500;600;700;800&display=swap",
      },
    ],

    appearance: {
      variables: {
        fontLineHeight: "1.2",
        borderRadius: "16px",
        colorBackground: "#F5EEFD",
        colorText: "#2E0F52",
        colorPrimaryText: "#2E0F52",
        colorTextSecondary: "#6B5690",
        colorTextPlaceholder: "#6B5690",
        fontSizeBase: "14px",
        colorPrimary: "#782DC8",
        fontFamily: "Manrope, system-ui, sans-serif",
        fontWeightNormal: "600",
      },
      theme: "flat",
      labels: "floating",
      rules: {
        ".Block": {
          backgroundColor: "var(--colorBackground)",
          boxShadow: "none",
          padding: "12px",
        },
        ".Input": {
          padding: "14px",
          fontWeight: "600",
          border: "2px solid transparent",
          boxShadow: "none",
        },
        ".Input:focus": {
          border: "2px solid #782DC8",
          boxShadow: "none",
        },
        ".Input--invalid": {
          boxShadow: "none",
        },

        ".Input:disabled, .Input--invalid:disabled": {
          color: "white",
        },
        ".Tab": {
          padding: "12px 14px 10px 14px",
          border: "2px solid #E9DEF8",
          backgroundColor: "#fff",
          boxShadow: "none",
          fontSize: "14px",
          fontWeight: "700",
        },
        ".Tab:hover": {
          border: "2px solid #CDB8EE",
          boxShadow: "none",
        },
        ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
          border: "2px solid #782DC8",
          backgroundColor: "#F5EEFD",
          boxShadow: "none",
        },
        ".Label": {
          fontWeight: "700",
        },
        ".TermsText": {
          // display: "none",
          opacity: "0 !important",
          fontSize: 0,
        },
      },
    },
  };
  // The full-screen paywall (UpgradeModal) shows the form on its dark page:
  // the same layout, its own colours.
  if (dark) {
    Object.assign(stripeoptions.appearance.variables, {
      colorBackground: "#3A2660",
      colorText: "#FFFFFF",
      colorPrimaryText: "#FFFFFF",
      colorTextSecondary: "#CDB8EE",
      colorTextPlaceholder: "#9C86C2",
      colorPrimary: "#D1FE17",
      colorIcon: "#CDB8EE",
    });
    Object.assign(stripeoptions.appearance.rules, {
      // Lighter than the page with an edge, so the fields read as fields on
      // the dark paywall instead of melting into it.
      ".Input": {
        ...stripeoptions.appearance.rules[".Input"],
        border: "2px solid #5B4485",
      },
      ".Input:focus": { border: "2px solid #D1FE17", boxShadow: "none" },
      ".Tab": {
        ...stripeoptions.appearance.rules[".Tab"],
        border: "2px solid #5B4485",
        backgroundColor: "#3A2660",
        color: "#FFFFFF",
      },
      ".Tab:hover": { border: "2px solid #7A63A6", boxShadow: "none" },
      ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
        border: "2px solid #D1FE17",
        backgroundColor: "#46306F",
        boxShadow: "none",
      },
    });
  }
  return (
    <>
      <Elements stripe={stripePromise} options={stripeoptions}>
        <StripePaymentElement
          newLayout={newLayout}
          price={price}
          currency={currency}
          intentType={intentType}
          submitLabel={submitLabel}
          formClassName={formClassName}
          submitWrapClassName={submitWrapClassName}
          footer={footer}
          onReady={onReady}
          express={express}
        />
      </Elements>
    </>
  );
};

/** Heading, Stripe form and the reassurance line, in design language F. */
export const CheckoutPanel = (props) => (
  <div className="tw-flex tw-flex-col tw-gap-4 tw-text-jam-ink">
    <div className="tw-mx-auto tw-h-[5px] tw-w-11 tw-rounded tw-bg-jam-line md:tw-hidden" />
    <h2 className="jam-display tw-m-0 tw-pr-12 tw-text-[30px] tw-font-extrabold tw-leading-[1.02] tw-tracking-[-0.04em] md:tw-text-[36px]">
      Checkout
    </h2>
    <CreditCheckout {...props} />
    <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-xs tw-font-bold tw-text-jam-muted md:tw-text-[13px]">
      <Lock size={13} strokeWidth={2.6} />
      Secure checkout with Stripe
    </div>
  </div>
);

const CreditCheckoutModal = ({ opened, setOpened, ...props }) => {
  return (
    <Dialog
      onOpenChange={(open) => setOpened(open)}
      preventClose
      open={opened}
      aria-hidden={opened ? false : true}
    >
      <DialogContent
        classNames={{
          overlay: "!tw-bg-jam-ink/40",
          close: "!tw-right-4 !tw-top-4",
        }}
        closeIcon={
          <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-ink tw-transition-colors hover:tw-bg-jam-line">
            <X size={18} strokeWidth={2.5} />
          </span>
        }
        className="tw-max-h-[94vh] tw-w-full tw-overflow-y-auto tw-rounded-t-[32px] !tw-border-0 !tw-bg-white !tw-p-[18px] !tw-pb-7 tw-text-jam-ink !tw-shadow-none md:!tw-w-[480px] md:!tw-max-w-[480px] md:!tw-rounded-[36px] md:!tw-p-[30px]"
      >
        <CheckoutPanel {...props} />
      </DialogContent>
    </Dialog>
  );
};

export default CreditCheckoutModal;
